"use client";

import { useEffect, useState, useRef } from "react";
import CdnImage from "@/components/CdnImage";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";

export default function UnlockPreview() {
  const [currentTime, setCurrentTime] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [rootMargin, setRootMargin] = useState("0px 0px -20% 0px");

  const controls = useAnimation();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Ajustar rootMargin según tamaño de pantalla
  useEffect(() => {
    if (typeof window !== "undefined") {
      const isMobile = window.innerWidth < 768;
      setRootMargin(isMobile ? "0px 0px -12% 0px" : "0px 0px -20% 0px");
    }
  }, []);

  const { ref, inView } = useInView({
    threshold: 0.8,
    rootMargin,
  });

  const imageWidth = 1080;
  const imageHeight = 2400;
  const aspectRatio = imageWidth / imageHeight;

  // Hora y fecha
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const h = now.getHours().toString().padStart(2, "0");
      const m = now.getMinutes().toString().padStart(2, "0");
      setCurrentTime(`${h}:${m}`);

      const options: Intl.DateTimeFormatOptions = {
        weekday: "short",
        month: "short",
        day: "numeric",
      };
      setCurrentDate(now.toLocaleDateString("en-US", options));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Scroll y control de vídeo
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (inView) {
        if (currentScrollY > lastScrollY && !isUnlocked) {
          setIsUnlocked(true);
          controls.start({
            y: "-110%",
            opacity: 0,
            transition: { duration: 1.2, ease: "easeInOut" },
          });

          // Solo play si está pausado
          const video = videoRef.current;
          if (video && video.paused) {
            video.play().catch(() => {});
          }
        }

        if (currentScrollY < lastScrollY && isUnlocked) {
          setIsUnlocked(false);
          controls.start({
            y: "0%",
            opacity: 1,
            transition: { duration: 1.2, ease: "easeInOut" },
          });

          // Solo pausa si no está ya pausado
          const video = videoRef.current;
          if (video && !video.paused) {
            video.pause();
          }
        }
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [inView, lastScrollY, controls, isUnlocked]);

  return (
    <div
      ref={ref}
      className="relative w-full max-w-[270px] mx-auto rounded-[5px] overflow-hidden shadow-2xl border border-gray-300"
      style={{ aspectRatio }}
    >
      {/* VIDEO - Placeholder Telegram */}
      <video
        ref={videoRef}
        src="https://telegram.org/img/t_logo.png"
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 object-cover w-full h-full"
        style={{ zIndex: 5 }}
      />

      {/* Imagen de fondo */}
      <CdnImage
        src="/pfp_bot_tg.png"
        alt="FrenFlow Preview"
        fill
        className="object-cover"
      />
      <div className="absolute inset-0 z-10" />

      {/* Overlay animado tipo bloqueo */}
      <motion.div
        animate={controls}
        initial={{ y: "0%", opacity: 1 }}
        className="absolute inset-0 z-20 flex flex-col justify-between p-6 bg-black/80 backdrop-blur-[6px] rounded-[5px] pointer-events-none"
      >
        {/* Notificación push */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
          className="absolute top-4 left-1/2 -translate-x-1/2 w-[90%] max-w-[320px] bg-white/80 backdrop-blur-md shadow-md rounded-[5px] px-4 py-3 flex items-center gap-3 z-30 border border-gray-200"
        >
          <CdnImage
            src="/brand/frenflow_logo.png"
            alt="FrenFlow Icon"
            width={32}
            height={32}
            className="p-1 bg-white rounded-[5px]"
          />
          <div className="text-xs leading-tight text-black">
            <p className="text-[11px] text-[#111729]"><span className="font-monument font-bold">FREN</span><span className="font-satoshi font-light">FLOW</span></p>
            <p className="text-[11px]">🎉 Your copy trade just earned +$247.50 (18.2% profit)</p>
          </div>
        </motion.div>

        {/* Hora y fecha */}
        <div className="mt-25 text-center text-white drop-shadow-[0_0_5px_rgba(40,127,113,0.8)]">
          <p className="text-5xl font-extrabold tracking-tight">{currentTime}</p>
          <p className="mt-1 text-xs uppercase">{currentDate}</p>
        </div>

        {/* Indicador swipe */}
        <div className="flex flex-col items-center text-white">
          <svg
            className="mb-1 animate-bounce"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <polyline points="6 13 12 19 18 13" />
          </svg>
          <span className="text-[10px] font-semibold tracking-wide uppercase">
            Unlocking...
          </span>
        </div>
      </motion.div>
    </div>
  );
}
